/**
 * Calendar days as YYYY-MM-DD text: the one place pages turn a date into a day and back.
 * A day has no time and no zone; show one with formatDay() from `@/lib/formatters`, and get
 * "today" from projectTodayYmd() in `@/lib/project-today` (the project's timezone), never the browser's.
 */

const YMD = /^(\d{4})-(\d{2})-(\d{2})$/;

/** A real calendar day written as YYYY-MM-DD ("2026-02-30" is not). */
export function isYmd(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const match = YMD.exec(value);
  if (!match) return false;
  const [y, m, d] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const date = new Date(Date.UTC(y, m - 1, d));
  return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d;
}

/**
 * The calendar day in a value the API sends for a date-only column: "2026-10-05", or the same day
 * as UTC midnight ("2026-10-05T00:00:00.000Z", how pg DATE columns arrive when not sent as text).
 * Null for anything else, so an instant is never cut to the wrong day by accident.
 */
export function toCalendarYmd(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  if (isYmd(value)) return value;
  const midnight = /^(\d{4}-\d{2}-\d{2})T00:00(?::00(?:\.0+)?)?(?:Z|[+-]00:?00)$/.exec(value);
  return midnight && isYmd(midnight[1]) ? midnight[1] : null;
}

/** The day as a Date at UTC midnight: format it with timeZone 'UTC' so it never slips a day. */
export function ymdToUtcDate(ymd: string): Date {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(Date.UTC(y!, (m ?? 1) - 1, d ?? 1));
}

/** A local Date (a date picker's) as YYYY-MM-DD. */
export function toYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** YYYY-MM-DD as a local Date (not UTC), for a date picker. */
export function parseYmd(ymd: string): Date {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y!, m! - 1, d!);
}

/** Add calendar days to a YYYY-MM-DD string (UTC date arithmetic). */
export function ymdAddDays(ymd: string, days: number): string {
  const date = ymdToUtcDate(ymd);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}
